import type { FontAwesomeIconProps } from '@fortawesome/react-fontawesome';
import { requestErrors } from 'helpers/api_errors';

export type SnackbarVariant = 'danger' | 'info' | 'success' | 'warning';
export type SnackbarPosition = 'left' | 'right';

export type ShowSnackbarOptions = {
  text: React.ReactNode;
  variant?: SnackbarVariant;
  delay?: number;
  autohide?: boolean;
  position?: SnackbarPosition;
  icon?: FontAwesomeIconProps['icon'];
};

export const SHOW_SNACKBAR_EVENT = 'ui:show-snackbar';

export const showSnackbar = (opts: ShowSnackbarOptions) => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SHOW_SNACKBAR_EVENT, { detail: opts }));
};

const QUEUED_SNACKBAR_KEY = 'ui:queued-snackbar';

export type QueuedSnackbarOptions = Omit<ShowSnackbarOptions, 'text' | 'icon'> & { text: string };

export const showSnackbarOnNextPage = (opts: QueuedSnackbarOptions) => {
  try {
    window.sessionStorage.setItem(QUEUED_SNACKBAR_KEY, JSON.stringify(opts));
  } catch {
    // Storage is blocked (site data off): lose the toast, not the caller's navigation.
  }
};

// Storage that is blocked or holds unparseable JSON must not take down the page's Snackbar.
export const takeSnackbarFromLastPage = (): QueuedSnackbarOptions | null => {
  try {
    const queued = window.sessionStorage.getItem(QUEUED_SNACKBAR_KEY);
    window.sessionStorage.removeItem(QUEUED_SNACKBAR_KEY);
    return queued ? JSON.parse(queued) : null;
  } catch {
    return null;
  }
};

// A failed helpers/api request as a danger snackbar: the server's messages, or the fallback.
export const showRequestFailure = (error: unknown, fallback: string) => {
  showSnackbar({ text: requestErrors(error, fallback).join(' '), variant: 'danger' });
};
